/**
 * UI store: 深浅色切换与全局弹层状态 (导航状态由 stores/router.ts 提供).
 * 用法: $store.ui.theme / $store.ui.toggleTheme() / $store.ui.openRedeem()
 */

import { request } from '../lib/api'
import { goTo, type RouteName } from './router'
import { applyTheme, resolveInitialTheme, type ThemeName } from '../lib/theme'

export interface UiStore {
  courseSearchOpen: boolean
  theme: ThemeName
  toggleTheme(): void
  purchaseEnabled: boolean
  purchaseReady: boolean
  syncPurchaseAvailability(userId: number | undefined): Promise<void>
  guardPurchaseRoute(route: RouteName): void
  /** 激活会员弹层: 状态驱动 (非路由), 因此会盖在当前页面之上 */
  redeemOpen: boolean
  openRedeem(): void
  closeRedeem(): void
}

export function uiStore(): UiStore {
  let ownerId: number | undefined
  let pending = false
  let version = 0
  return {
    courseSearchOpen: false,
    purchaseEnabled: false,
    purchaseReady: false,

    async syncPurchaseAvailability(userId) {
      if (ownerId === userId && (pending || this.purchaseReady)) return
      ownerId = userId
      const current = ++version
      this.purchaseEnabled = false
      this.purchaseReady = false
      pending = Boolean(userId)
      if (!userId) {
        this.purchaseReady = true
        return
      }
      try {
        const result = await request<{ enabled: boolean }>('/membership/products')
        if (current === version) this.purchaseEnabled = result.enabled === true
      } catch {
        // 开关读取失败时默认隐藏, 不把不可付款的入口暴露给用户.
      } finally {
        if (current === version) {
          pending = false
          this.purchaseReady = true
        }
      }
    },

    guardPurchaseRoute(route) {
      if (this.purchaseReady && !this.purchaseEnabled && ownerId !== undefined && (route === 'membership' || route === 'orders')) {
        goTo('profile')
      }
    },
    theme: resolveInitialTheme(),

    toggleTheme() {
      this.theme = this.theme === 'dark' ? 'light' : 'dark'
      applyTheme(this.theme)
    },

    redeemOpen: false,

    openRedeem() {
      this.redeemOpen = true
    },

    closeRedeem() {
      this.redeemOpen = false
    },
  }
}

/** 供 TS 页面组件调用: 取 Alpine 上的响应式 store 实例 */
export function uiRef(): UiStore | undefined {
  return window.Alpine?.store('ui') as UiStore | undefined
}
